import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Send, X, Paperclip, Search, Users, Circle, Download, FileText, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError } from "../../lib/api";
import { mediaUrl } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";

const ease = [0.16, 1, 0.3, 1];
const AV = ["bg-violet", "bg-navy", "bg-emerald-500", "bg-gold", "bg-blue-600", "bg-rose-500"];
const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const color = (id) => AV[(id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AV.length];

function Avatar({ name, id, online, size = "w-10 h-10" }) {
  return (
    <div className="relative shrink-0">
      <div className={`${size} ${color(id)} rounded-full flex items-center justify-center text-white text-xs font-medium`}>{initials(name)}</div>
      {online !== undefined && (
        <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-cream ${online ? "bg-emerald-500" : "bg-navy/25"}`} />
      )}
    </div>
  );
}

export default function Chat() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [typing, setTyping] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [modal, setModal] = useState(false);
  const scrollRef = useRef(null);
  const typingTimer = useRef(null);

  const loadConvos = useCallback(() => {
    api.get("/chat/conversations").then((r) => setConvos(r.data)).catch(() => {});
  }, []);

  const loadThread = useCallback((cid) => {
    if (!cid) return;
    api.get(`/chat/conversations/${cid}/messages`).then((r) => setThread(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => { const iv = setInterval(loadConvos, 8000); return () => clearInterval(iv); }, [loadConvos]);

  useEffect(() => { if (activeId) loadThread(activeId); }, [activeId, loadThread]);

  // poll active thread + typing/online state
  useEffect(() => {
    if (!activeId) return;
    const iv = setInterval(() => {
      loadThread(activeId);
      api.get(`/chat/conversations/${activeId}/state`).then((r) => setTyping(r.data.typing || [])).catch(() => {});
    }, 3000);
    return () => clearInterval(iv);
  }, [activeId, loadThread]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread?.messages?.length, typing.length]);

  const onType = () => {
    if (!activeId) return;
    if (typingTimer.current) return;
    api.post(`/chat/conversations/${activeId}/typing`).catch(() => {});
    typingTimer.current = setTimeout(() => { typingTimer.current = null; }, 4000);
  };

  const send = async (attachments = []) => {
    if ((!text.trim() && attachments.length === 0) || !activeId) return;
    setSending(true);
    try {
      await api.post(`/chat/conversations/${activeId}/messages`, { body: text.trim(), attachments });
      setText("");
      loadThread(activeId);
      loadConvos();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setSending(false); }
  };

  const onAttach = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/chat/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await send([data]);
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Error"); }
  };

  const openConvo = (c) => { setActiveId(c.id); setTyping([]); };

  return (
    <OSLayout title={t("os.chat.title")}>
      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-11rem)]" data-testid="chat-page">
        {/* Conversations list */}
        <div className={`bg-white border border-navy/10 rounded-xl flex flex-col overflow-hidden ${activeId ? "hidden md:flex" : "flex"}`}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-navy/10">
            <span className="font-medium">{t("os.chat.title")}</span>
            <button data-testid="chat-new-btn" onClick={() => setModal(true)}
              className="inline-flex items-center gap-1.5 text-xs rounded-full bg-gradient-violet text-white px-3 py-1.5 hover:opacity-90 transition-opacity">
              <Plus className="w-3.5 h-3.5" /> {t("os.chat.new")}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {convos.length === 0 ? (
              <div className="p-6 text-sm text-navy/40" data-testid="chat-convos-empty">{t("os.chat.empty")}</div>
            ) : convos.map((c) => (
              <button key={c.id} data-testid={`chat-convo-${c.id}`} onClick={() => openConvo(c)}
                className={`w-full flex items-center gap-3 px-4 py-3 border-b border-navy/5 text-left hover:bg-cream/60 transition-colors ${activeId === c.id ? "bg-cream" : ""}`}>
                {c.type === "group"
                  ? <div className="w-10 h-10 rounded-full bg-navy/10 text-navy flex items-center justify-center shrink-0"><Users className="w-4 h-4" /></div>
                  : <Avatar name={c.name} id={c.other_id} online={c.online} />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{c.name}</span>
                    {c.unread > 0 && <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-violet text-white text-[10px] flex items-center justify-center">{c.unread}</span>}
                  </div>
                  <div className="text-xs text-navy/45 truncate">{c.last_message || "—"}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className={`bg-white border border-navy/10 rounded-xl flex flex-col overflow-hidden ${activeId ? "flex" : "hidden md:flex"}`}>
          {!thread ? (
            <div className="flex-1 flex flex-col items-center justify-center text-navy/35 gap-3" data-testid="chat-empty-state">
              <MessageCircle className="w-10 h-10" />
              <p className="text-sm">{t("os.chat.pickConvo")}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-4 py-3 border-b border-navy/10">
                <button className="md:hidden text-navy/50" onClick={() => setActiveId(null)}><X className="w-5 h-5" /></button>
                {thread.conversation.type === "group"
                  ? <div className="w-9 h-9 rounded-full bg-navy/10 text-navy flex items-center justify-center"><Users className="w-4 h-4" /></div>
                  : <Avatar name={thread.conversation.name} id={thread.conversation.other_id} online={thread.conversation.online} size="w-9 h-9" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{thread.conversation.name}</div>
                  <div className="text-xs text-navy/45 truncate flex items-center gap-1.5">
                    {thread.conversation.type === "group"
                      ? `${thread.members.length} ${t("os.chat.members").toLowerCase()}`
                      : (thread.conversation.online ? <><Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" /> {t("os.chat.online")}</> : t("os.chat.offline"))}
                  </div>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-cream/40">
                {thread.messages.length === 0 && <p className="text-center text-sm text-navy/35 py-8">{t("os.chat.noMessages")}</p>}
                {thread.messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`}>
                      {!mine && <Avatar name={m.sender_name} id={m.sender_id} size="w-8 h-8" />}
                      <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                        {thread.conversation.type === "group" && !mine && <span className="text-[11px] text-navy/45 mb-0.5 px-1">{m.sender_name}</span>}
                        <div className={`rounded-2xl px-4 py-2.5 ${mine ? "bg-gradient-violet text-white" : "bg-white border border-navy/10 text-navy"}`}>
                          {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                          {(m.attachments || []).map((a, i) => (
                            <Attachment key={i} a={a} mine={mine} t={t} />
                          ))}
                        </div>
                        <span className="text-[10px] text-navy/35 mt-1 px-1">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    </div>
                  );
                })}
                {typing.length > 0 && (
                  <div className="text-xs text-navy/45 italic px-1" data-testid="chat-typing">{typing.join(", ")} {t("os.chat.typing")}</div>
                )}
              </div>

              <div className="flex items-center gap-2 p-3 border-t border-navy/10">
                <label data-testid="chat-attach" className="shrink-0 w-9 h-9 rounded-full border border-navy/10 flex items-center justify-center text-navy/50 hover:text-violet hover:border-violet/40 cursor-pointer transition-colors">
                  <Paperclip className="w-4 h-4" />
                  <input type="file" className="hidden" onChange={onAttach} data-testid="chat-file-input" />
                </label>
                <input data-testid="chat-input" value={text}
                  onChange={(e) => { setText(e.target.value); onType(); }}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={t("os.chat.placeholder")}
                  className="flex-1 bg-cream border border-navy/10 rounded-full px-4 py-2.5 outline-none focus:border-violet transition-colors text-sm" />
                <button data-testid="chat-send" onClick={() => send()} disabled={sending}
                  className="shrink-0 w-10 h-10 rounded-full bg-gradient-violet text-white flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-50">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {modal && <NewChatModal t={t} onClose={() => setModal(false)}
          onCreated={(c) => { setModal(false); loadConvos(); setActiveId(c.id); }} />}
      </AnimatePresence>
    </OSLayout>
  );
}

function Attachment({ a, mine, t }) {
  const href = mediaUrl(a.url);
  if (a.is_image) {
    return <a href={href} target="_blank" rel="noreferrer" className="block mt-1"><img src={href} alt={a.name} className="rounded-lg max-h-52 object-cover" /></a>;
  }
  return (
    <a href={`${href}?download=1`} data-testid="chat-attach-file"
      className={`mt-1 flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${mine ? "bg-white/20" : "bg-cream border border-navy/10"}`}>
      <FileText className="w-4 h-4 shrink-0" />
      <span className="truncate flex-1">{a.name}</span>
      <Download className="w-4 h-4 shrink-0" />
    </a>
  );
}

function NewChatModal({ onClose, onCreated, t }) {
  const [tab, setTab] = useState("dm");
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/chat/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const filtered = users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()));

  const startDm = async (u) => {
    setBusy(true);
    try { const { data } = await api.post("/chat/conversations/dm", { user_id: u.id }); onCreated(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); setBusy(false); }
  };

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const createGroup = async () => {
    if (!groupName.trim() || selected.length === 0) return;
    setBusy(true);
    try { const { data } = await api.post("/chat/conversations/group", { name: groupName.trim(), member_ids: selected }); onCreated(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid="chat-new-modal" className="w-full max-w-md bg-cream rounded-xl p-6 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">{t("os.chat.new")}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-2 mb-4">
          {["dm", "group"].map((tk) => (
            <button key={tk} data-testid={`chat-tab-${tk}`} onClick={() => setTab(tk)}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${tab === tk ? "bg-navy text-cream" : "border border-navy/15 text-navy/60 hover:text-navy"}`}>
              {t(`os.chat.${tk}`)}
            </button>
          ))}
        </div>
        {tab === "group" && (
          <input data-testid="chat-group-name" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder={t("os.chat.groupName")}
            className="w-full bg-white border border-navy/10 rounded-lg px-4 py-2.5 outline-none focus:border-violet transition-colors mb-3" />
        )}
        <div className="relative mb-3">
          <Search className="w-4 h-4 text-navy/30 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("os.chat.search")}
            className="w-full bg-white border border-navy/10 rounded-lg pl-9 pr-4 py-2.5 outline-none focus:border-violet transition-colors" />
        </div>
        <div className="flex-1 overflow-y-auto space-y-1.5">
          {filtered.map((u) => (
            <button key={u.id} data-testid={`chat-user-${u.id}`}
              onClick={() => tab === "dm" ? startDm(u) : toggle(u.id)} disabled={busy}
              className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors ${tab === "group" && selected.includes(u.id) ? "bg-violet/10 border border-violet/30" : "bg-white border border-navy/10 hover:border-violet/40"}`}>
              <Avatar name={u.name} id={u.id} online={u.online} size="w-9 h-9" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{u.name}</div>
                <div className="text-xs text-navy/45 truncate">{u.role}</div>
              </div>
              {tab === "group" && selected.includes(u.id) && <Circle className="w-4 h-4 fill-violet text-violet" />}
            </button>
          ))}
        </div>
        {tab === "group" && (
          <button data-testid="chat-create-group" onClick={createGroup} disabled={busy || !groupName.trim() || selected.length === 0}
            className="mt-4 w-full rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 transition-opacity disabled:opacity-40">
            {t("os.chat.create")} ({selected.length})
          </button>
        )}
      </motion.div>
    </motion.div>
  );
}
